// src/components/common/vibe/VoiceToHR.js
import React, { useState } from 'react';
import { MessageSquare, Send, X } from 'lucide-react';
import vibeService from '../../../services/vibe.service';

const SUGGESTION_CATEGORIES = [
    { value: 'fun_idea', label: 'Fun Idea', emoji: '🎉' },
    { value: 'appreciation', label: 'Appreciation', emoji: '💝' },
    { value: 'improvement', label: 'Improvement', emoji: '💡' },
    { value: 'concern', label: 'Concern', emoji: '⚠️' }
];

const VoiceToHR = ({ theme }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isAnonymous, setIsAnonymous] = useState(false);
    const [category, setCategory] = useState('');
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!content.trim() || !category) {
            alert('Please select a category and enter your message');
            return;
        }

        setLoading(true);
        try {
            // //console.log('📝 Submitting Voice to HR suggestion:', {
            //     category,
            //     is_anonymous: isAnonymous,
            //     contentLength: content.trim().length
            // });

            const response = await vibeService.submitSuggestion({
                content: content.trim(),
                suggestion_category: category,
                is_anonymous: isAnonymous
            });

            //console.log('✅ Suggestion submitted successfully:', response);

            setSubmitSuccess(true);
            setContent('');
            setCategory('');
            setIsAnonymous(false);

            setTimeout(() => {
                setSubmitSuccess(false);
                setIsExpanded(false);
            }, 2000);
        } catch (error) {
            console.error('❌ Error submitting suggestion:', error);
            alert('Failed to submit. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setContent('');
        setCategory('');
        setIsAnonymous(false);
        setIsExpanded(false);
        setSubmitSuccess(false);
    };

    return (
        <div className={`rounded-xl border ${
            theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
            {/* Header */}
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className={`w-full flex items-center justify-between p-4 ${
                    theme === 'dark' ? 'hover:bg-gray-750' : 'hover:bg-gray-50'
                } rounded-t-xl transition-colors`}
            >
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${
                        theme === 'dark' ? 'bg-blue-900/30' : 'bg-blue-100'
                    }`}>
                        <MessageSquare className={`w-5 h-5 ${
                            theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                        }`} />
                    </div>
                    <div className="text-left">
                        <h3 className={`font-semibold ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                            Voice to HR
                        </h3>
                        <p className={`text-xs ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                            Share your thoughts anonymously
                        </p>
                    </div>
                </div>
                <div className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </div>
            </button>

            {/* Expanded Form */}
            {isExpanded && (
                <form onSubmit={handleSubmit} className="p-4 pt-0 space-y-4">
                    {submitSuccess ? (
                        <div className={`p-6 rounded-lg text-center ${
                            theme === 'dark' ? 'bg-green-900/30' : 'bg-green-50'
                        }`}>
                            <div className="text-4xl mb-2">✅</div>
                            <p className={`font-medium ${
                                theme === 'dark' ? 'text-green-400' : 'text-green-700'
                            }`}>
                                Submitted Successfully!
                            </p>
                            <p className={`text-sm mt-1 ${
                                theme === 'dark' ? 'text-green-300' : 'text-green-600'
                            }`}>
                                Your voice has been heard
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Category Selection */}
                            <div>
                                <label className={`block text-sm font-medium mb-2 ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>
                                    Category <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={category}
                                    onChange={(e) => setCategory(e.target.value)}
                                    required
                                    className={`w-full px-3 py-2 rounded-lg border ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white'
                                            : 'bg-white border-gray-300 text-gray-900'
                                    }`}
                                >
                                    <option value="">Select a category...</option>
                                    {SUGGESTION_CATEGORIES.map((cat) => (
                                        <option key={cat.value} value={cat.value}>
                                            {cat.emoji} {cat.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Message */}
                            <div>
                                <label className={`block text-sm font-medium mb-2 ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>
                                    Your Message <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    value={content}
                                    onChange={(e) => setContent(e.target.value)}
                                    placeholder="Share your thoughts, ideas, or concerns..."
                                    rows={4}
                                    required
                                    maxLength={1000}
                                    className={`w-full px-3 py-2 rounded-lg border resize-none ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                    }`}
                                />
                                <div className={`text-xs text-right mt-1 ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                }`}>
                                    {content.length}/1000 characters
                                </div>
                            </div>

                            {/* Anonymous Checkbox */}
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="anonymous"
                                    checked={isAnonymous}
                                    onChange={(e) => setIsAnonymous(e.target.checked)}
                                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <label
                                    htmlFor="anonymous"
                                    className={`text-sm ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}
                                >
                                    Submit anonymously
                                </label>
                            </div>

                            {/* Info Message */}
                            <div className={`p-3 rounded-lg text-xs ${
                                theme === 'dark' ? 'bg-blue-900/20 text-blue-300' : 'bg-blue-50 text-blue-700'
                            }`}>
                                {isAnonymous ? (
                                    <p>🔒 Your submission will be completely anonymous. HR won't know who sent it.</p>
                                ) : (
                                    <p>👤 Your name will be visible to HR admins.</p>
                                )}
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-2 pt-2">
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading || !content.trim() || !category}
                                    className={`flex-1 px-4 py-2 rounded-lg font-medium text-white flex items-center justify-center gap-2 ${
                                        loading || !content.trim() || !category
                                            ? 'bg-blue-400 cursor-not-allowed'
                                            : 'bg-blue-600 hover:bg-blue-700'
                                    }`}
                                >
                                    {loading ? (
                                        'Submitting...'
                                    ) : (
                                        <>
                                            <Send className="w-4 h-4" />
                                            Submit
                                        </>
                                    )}
                                </button>
                            </div>
                        </>
                    )}
                </form>
            )}
        </div>
    );
};

export default VoiceToHR;
